import React, { useState, useEffect, Suspense } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { CenteredSpinner } from '@/components/ui/CenteredSpinner';

import { useRole, ROLE_LABELS, ALL_ROLES } from '@/contexts/RoleContext';
import { useAuth } from '@/contexts/AuthContext';
import { useBranding } from '@/contexts/BrandingContext';
import { useToolSettings } from '@/hooks/useToolSettings';
import { getEffectiveToolStatus } from '@/services/toolSettings';
import { tools } from '@/data/tools';
// Viewport simulation removed - only role simulation is used
import { useCurrentProfile } from '@/hooks/useCurrentProfile';
import { 
  Home, 
  ShoppingBag, 
  Terminal, 
  User, 
  ChevronDown,
  Eye,
  X,
  Flame,
  LogIn,
  Hammer,
  Lock,
  Menu,
  GraduationCap,
  
  Power,
  Settings
} from 'lucide-react';
import { usePVRedirect } from '@/hooks/usePVRedirect';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ProfileAvatar } from '@/components/ui/profile-avatar';
import { cn } from '@/lib/utils';
import NotificationBell from '@/components/NotificationBell';
import SoundPermissionModal from '@/components/SoundPermissionModal';
import { prefetchPageComponent, prefetchRouteData } from '@/hooks/useComponentPrefetch';
import { useQueryClient } from '@tanstack/react-query';
import ModoForjaButton from '@/components/modoforja/ModoForjaButton';
import Footer from '@/components/Footer';
import NavSparksIndicator from '@/components/NavSparksIndicator';
import PWAReinstallBanner from '@/components/PWAReinstallBanner';

/**
 * PREFETCH ON HOVER: Ao adicionar novos links de navegação,
 * incluir onMouseEnter={() => prefetchPageComponent('tool-id')}
 * para pré-carregar o componente quando o mouse passar sobre o link.
 * 
 * Ver: src/hooks/useComponentPrefetch.ts para a lista completa de IDs.
 */
const navItems = [
  { label: 'Home', path: '/', icon: Home, toolId: null },
  { label: 'Minha Forja', path: '/minha-forja', icon: Hammer, toolId: 'minha-forja' },
  { label: 'Marketplace', path: '/marketplace', icon: ShoppingBag, toolId: 'marketplace' },
  { label: 'Comunidade', path: '/comunidade', icon: GraduationCap, toolId: 'comunidade' },
];

export default function Layout() {
  const location = useLocation();
  const { 
    currentRole,
    effectiveRole, 
    isAdmin, 
    isSimulating, 
    simulatedRole,
    setSimulatedRole,
  } = useRole();
  const { user, signOut } = useAuth();
  const { logoUrl, isLoading } = useBranding();
  // Defer tool settings fetch until auth is resolved (avoids wasted query during boot)
  const { getSettingByToolId } = useToolSettings({ enabled: !!user });
  const { redirectToPV } = usePVRedirect();
  const queryClient = useQueryClient();
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Pre-compute Sparks active status (used in both desktop and mobile)
  const isSparksActive = (() => {
    const faiscasTool = tools.find(t => t.id === 'faiscas');
    const faiscasSetting = getSettingByToolId('faiscas');
    const faiscasStatus = getEffectiveToolStatus('faiscas', effectiveRole, faiscasSetting, 'active', faiscasTool?.allowedRoles || []);
    return faiscasStatus.status === 'active';
  })();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Profile cached with unified hook (syncs with ProfileHero saves)
  const { data: currentProfile } = useCurrentProfile(user?.id);
  const avatarUrl = currentProfile?.avatar_url || null;
  const userName = currentProfile?.display_name || user?.email || 'Usuário';

  // All navigation items including Terminal for mobile
  const allNavItems = [
    ...navItems,
    ...(effectiveRole === 'admin' || effectiveRole === 'staff' 
      ? [{ label: 'Terminal', path: '/command-terminal', icon: Terminal, toolId: null }] 
      : [])
  ];

  return (
    <div className="h-screen h-[100dvh] flex flex-col bg-background overflow-hidden">
      {/* Top Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-nav safe-area-pt">
        <div className="container mx-auto px-3 md:px-4">
          <div className="flex items-center justify-between h-14 md:h-16 py-2 md:py-3 relative">
            {/* Logo + Role Simulator (Admin/Staff only) */}
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-2.5 group">
                <div className={cn(
                  "relative w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden transition-all duration-300",
                  !logoUrl && !isLoading && "bg-gradient-to-br from-primary to-primary/70 shadow-glow-sm",
                  isLoading && "bg-secondary/50"
                )}>
                  {isLoading ? (
                    <Spinner size="sm" />
                  ) : logoUrl ? (
                    <img 
                      src={logoUrl} 
                      alt="Logo" 
                      className="w-full h-full object-contain" 
                    />
                  ) : (
                    <Flame className="w-5 h-5 text-primary-foreground relative z-10" />
                  )}
                  {!logoUrl && !isLoading && <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity" />}
                </div>
                <span className="font-display font-bold text-lg text-foreground tracking-tight">
                  Forja<span className="text-gradient-primary">Hub</span>
                </span>
              </Link>

              {/* Role/Viewport Simulator - Admin/Staff only */}
              {(currentRole === 'admin' || currentRole === 'staff') && (
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className={cn(
                        "gap-1.5 rounded-lg border-border/50 bg-secondary/50 hover:bg-secondary h-9 text-xs px-3 hidden md:flex",
                        isSimulating && "border-primary/50 bg-primary/10"
                      )}
                    >
                      <Eye className="w-4 h-4" />
                      <span className="hidden sm:inline">
                        {isSimulating ? ROLE_LABELS[simulatedRole!] : 'Simular'}
                      </span>
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-52 rounded-lg">
                    {/* Role Simulation */}
                    <DropdownMenuLabel className="text-xs text-muted-foreground">Simular Role</DropdownMenuLabel>
                    {isSimulating && (
                      <>
                        <DropdownMenuItem onClick={() => setSimulatedRole(null)} className="rounded-md text-xs">
                          <X className="w-4 h-4 mr-2" />
                          Parar Simulação
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    {ALL_ROLES.filter(r => r !== 'admin').map((role) => (
                      <DropdownMenuItem 
                        key={role} 
                        onClick={() => setSimulatedRole(role)}
                        className={cn("rounded-md text-xs", simulatedRole === role && "bg-primary/10 text-primary")}
                      >
                        {ROLE_LABELS[role]}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Sparks Indicator - Desktop only (mobile instance is in right side) */}
              {user && isSparksActive && (
                <div className="hidden md:block">
                  <NavSparksIndicator />
                </div>
              )}
            </div>

            {/* Center Navigation */}
            <nav className="hidden md:flex items-center gap-1 bg-secondary/50 rounded-full px-1.5 py-1 border border-border/50 absolute left-1/2 -translate-x-1/2 z-0">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path || (item.path === '/comunidade' && location.pathname.startsWith('/comunidade'));
                
                // Check tool access status
                let isBlocked = false;
                let blockMessage = '';
                
                if (item.toolId) {
                  const tool = tools.find(t => t.id === item.toolId);
                  const setting = getSettingByToolId(item.toolId);
                  // CRITICAL: Pass staticAllowedRoles for proper fallback
                  const effectiveStatus = getEffectiveToolStatus(
                    item.toolId,
                    effectiveRole,
                    setting,
                    tool?.status || 'active',
                    tool?.allowedRoles || []
                  );
                  
                  isBlocked = effectiveStatus.status === 'disabled' || effectiveStatus.status === 'locked';
                  blockMessage = effectiveStatus.status === 'locked'
                    ? 'Acesso restrito'
                    : effectiveStatus.message || 'Em atualização';
                }
                
                
                if (isBlocked) {
                  return (
                    <button
                      key={item.path}
                      onClick={() => {
                        toast.error(blockMessage || 'Acesso restrito');
                        redirectToPV('nav-blocked');
                      }}
                      className={cn(
                        'flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium',
                        'text-muted-foreground/50 cursor-not-allowed'
                      )}
                    >
                      <Lock className="w-3 h-3" />
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </button>
                  );
                }
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onMouseEnter={() => {
                      if (item.toolId) {
                        prefetchPageComponent(item.toolId);
                        prefetchRouteData(item.toolId, queryClient);
                      }
                    }}
                    className={cn(
                      'flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-300',
                      isActive 
                        ? 'bg-gradient-to-r from-primary to-primary/80 text-primary-foreground shadow-glow-sm' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                    )}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
              {/* Terminal - Only Admin and Staff have access */}
              {(effectiveRole === 'admin' || effectiveRole === 'staff') && (
                <Link
                  to="/command-terminal"
                  onMouseEnter={() => { prefetchPageComponent('command-terminal'); prefetchRouteData('command-terminal', queryClient); }}
                  className={cn(
                    'flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-300',
                    location.pathname.startsWith('/command-terminal')
                      ? 'bg-gradient-to-r from-primary to-primary/80 text-primary-foreground shadow-glow-sm' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                  )}
                >
                  <Terminal className="w-4 h-4" />
                  Terminal
                </Link>
              )}
            </nav>

            {/* Right Side */}
            <div className="flex items-center gap-2 md:gap-3 relative z-10">
              {/* Sparks Indicator - Mobile */}
              {user && isSparksActive && (
                <div className="md:hidden">
                  <NavSparksIndicator />
                </div>
              )}

              {/* Flow Mode Button - Available for all logged users including visitors */}
              {user && (
                <ModoForjaButton />
              )}
              
              {/* Notification Bell */}
              {user && <NotificationBell />}
              
              {/* User Profile - Desktop only */}
              <div className="hidden md:flex items-center gap-2 pl-3 border-l border-border/50">
                {user ? (
                  <>
                    <div className="text-right hidden sm:block">
                      <p className="text-xs font-semibold text-foreground leading-tight">{userName}</p>
                      <p className="text-[10px] text-muted-foreground">{ROLE_LABELS[effectiveRole]}</p>
                    </div>
                    
                    {/* Avatar - Link direto para perfil */}
                    <Link 
                      to="/profile" 
                      onMouseEnter={() => prefetchPageComponent('profile')}
                      className="group"
                    >
                      <ProfileAvatar src={avatarUrl} name={userName} className="w-9 h-9 border border-border/50 cursor-pointer hover:border-primary/50 hover:ring-2 hover:ring-primary/20 transition-all" />
                    </Link>
                    
                    {/* Botão de Logout - Power button */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => signOut()}
                      className="h-9 w-9 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                      title="Sair da conta"
                      aria-label="Sair da conta"
                    >
                      <Power className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <Link to="/auth">
                    <Button variant="outline" size="sm" className="gap-1.5 rounded-lg h-9 text-xs px-3">
                      <LogIn className="w-4 h-4" />
                      <span className="hidden sm:inline">Entrar</span>
                    </Button>
                  </Link>
                )}
              </div>

              {/* Mobile Menu Button */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden h-10 w-10 touch-target" aria-label="Abrir menu de navegação">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[85vw] max-w-80 p-0 safe-area-inset [&>button]:top-[18px]">
                  <SheetHeader className="p-4 border-b border-border/50">
                    <SheetTitle className="flex items-center gap-3">
                      {user ? (
                        <>
                          <Link 
                            to="/profile" 
                            onClick={() => setMobileMenuOpen(false)}
                            className="group"
                          >
                            <ProfileAvatar src={avatarUrl} name={userName} className="w-10 h-10 border border-border/50 hover:border-primary/50 hover:ring-2 hover:ring-primary/20 transition-all" />
                          </Link>
                          <div className="text-left flex-1">
                            <p className="text-sm font-semibold">{userName}</p>
                            <p className="text-xs text-muted-foreground">{ROLE_LABELS[effectiveRole]}</p>
                          </div>
                          {/* Power button mobile */}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => signOut()}
                            className="h-9 w-9 mr-8 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                            title="Sair da conta"
                            aria-label="Sair da conta"
                          >
                            <Power className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <span>Menu</span>
                      )}
                    </SheetTitle>
                  </SheetHeader>
                  
                  <div className="p-4 space-y-1 mobile-drawer-content safe-area-pb">
                    {/* Navigation Links */}
                    {allNavItems.map((item) => {
                      const isActive = item.path === '/command-terminal' 
                        ? location.pathname.startsWith('/command-terminal')
                        : item.path === '/comunidade'
                        ? location.pathname.startsWith('/comunidade')
                        : location.pathname === item.path;
                      
                      // Check tool access status
                      let isBlocked = false;
                      let blockMessage = '';
                      
                      if ('toolId' in item && item.toolId) {
                        const tool = tools.find(t => t.id === item.toolId);
                        const setting = getSettingByToolId(item.toolId);
                        // CRITICAL: Pass staticAllowedRoles for proper fallback
                        const effectiveStatus = getEffectiveToolStatus(
                          item.toolId,
                          effectiveRole,
                          setting,
                          tool?.status || 'active',
                          tool?.allowedRoles || []
                        );
                        
                        isBlocked = effectiveStatus.status === 'disabled' || effectiveStatus.status === 'locked';
                        blockMessage = effectiveStatus.status === 'locked'
                          ? 'Acesso restrito'
                          : effectiveStatus.message || 'Em atualização';
                      }
                      
                      
                      if (isBlocked) {
                        return (
                          <button
                            key={item.path}
                            onClick={() => {
                              toast.error(blockMessage || 'Acesso restrito');
                              redirectToPV('nav-blocked');
                            }}
                            className={cn(
                              "flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-colors min-h-[48px]",
                              "text-muted-foreground/50 cursor-not-allowed"
                            )}
                          >
                            <Lock className="w-5 h-5" />
                            <item.icon className="w-5 h-5" />
                            {item.label}
                          </button>
                        );
                      }
                      
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={cn(
                            "flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-colors min-h-[48px]",
                            isActive 
                              ? "bg-primary text-primary-foreground" 
                              : "text-foreground hover:bg-secondary"
                          )}
                        >
                          <item.icon className="w-5 h-5" />
                          {item.label}
                        </Link>
                      );
                    })}
                    
                    {/* Divider */}
                    <div className="my-3 border-t border-border/50" />
                    
                    {/* Role Simulator for mobile (Admin/Staff only) */}
                    {(currentRole === 'admin' || currentRole === 'staff') && (
                      <div className="space-y-2">
                        <p className="px-4 text-xs text-muted-foreground font-medium">Simular como</p>
                        {isSimulating && (
                          <button
                            onClick={() => setSimulatedRole(null)}
                            className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm w-full text-left text-destructive hover:bg-destructive/10 transition-colors"
                          >
                            <X className="w-4 h-4" />
                            Parar Simulação
                          </button>
                        )}
                        {ALL_ROLES.filter(r => r !== 'admin').map((role) => (
                          <button
                            key={role}
                            onClick={() => setSimulatedRole(role)}
                            className={cn(
                              "flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm w-full text-left transition-colors",
                              simulatedRole === role 
                                ? "bg-primary/10 text-primary" 
                                : "text-foreground hover:bg-secondary"
                            )}
                          >
                            <Eye className="w-4 h-4" />
                            {ROLE_LABELS[role]}
                          </button>
                        ))}
                        <div className="my-3 border-t border-border/50" />
                      </div>
                    )}
                    
                    {/* User Actions */}
                    {user ? (
                      <Link
                        to="/profile"
                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground hover:bg-secondary transition-colors touch-target"
                      >
                        <Settings className="w-5 h-5" />
                        Meu Perfil
                      </Link>
                    ) : (
                      <Link
                        to="/auth"
                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium bg-primary text-primary-foreground touch-target"
                      >
                        <LogIn className="w-5 h-5" />
                        Entrar
                      </Link>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>

        {/* Simulation Banner - Role */}
        {isSimulating && (
          <div className="bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 border-t border-primary/20 px-4 py-1.5">
            <div className="container mx-auto flex items-center justify-center gap-2 text-xs">
              <Flame className="w-4 h-4 text-primary" />
              <span className="text-primary font-medium">
                Simulação de Role — <strong>{ROLE_LABELS[simulatedRole!]}</strong>
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setSimulatedRole(null)}
                className="ml-2 h-6 text-xs text-primary hover:text-primary hover:bg-primary/20 rounded-full px-2"
              >
                Encerrar
              </Button>
            </div>
          </div>
        )}
        
      </header>

      {/* Main Content */}
      <main 
        className="flex-1 overflow-x-hidden overflow-y-auto"
        style={{ paddingTop: isSimulating ? 'calc(3.5rem + 1.75rem + env(safe-area-inset-top, 0px))' : 'calc(3.5rem + env(safe-area-inset-top, 0px))' }}
      >
        {/* PWA Reinstall Banner (standalone only) */}
        <PWAReinstallBanner />
        <Suspense fallback={<CenteredSpinner fullScreen />}>
          <Outlet />
        </Suspense>
        {/* Global Footer — hidden inside Comunidade */}
        {!location.pathname.startsWith('/comunidade') && <Footer />}
      </main>

      {/* Sound Permission Modal */}
      {user && <SoundPermissionModal />}
    </div>
  );
}
